import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";

import MelhorEnvioSaldoCard from "components/MelhorEnvioSaldoCard";
import MDAvatar from "components/MDAvatar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { setMessage, useMaterialUIController } from "context";
import { cepApplyMask } from "helpers/utils";
import type { IMelhorEnvioConfig } from "interfaces/loja";
import type { IMelhorEnvioServico } from "interfaces/loja/freteMelhorEnvio";
import {
  createOrUpdateMelhorEnvioConfig,
  deleteMelhorEnvioConfig,
  getMelhorEnvioConfig,
  getMelhorEnvioOAuthStartUrl,
  getMelhorEnvioServicos,
} from "services/lojaConfig";
import { getCep } from "services/uteis";
import { siteConfig } from "types/siteConfig";
import { LojaPageContainer } from "../common";
import ConfigFormSection from "./components/ConfigFormSection";
import MelhorEnvioDeleteDialog from "./components/MelhorEnvioDeleteDialog";
import {
  DEFAULT_MELHOR_ENVIO_CONFIG,
  buildMelhorEnvioPayload,
  computeMelhorEnvioStats,
  normalizeMelhorEnvioConfig,
  validateMelhorEnvioConfig,
} from "./components/melhor-envio.helpers";
import {
  ME_ACCENT,
  emptyStateSx,
  infoBannerSx,
  oauthButtonSx,
  saveButtonSx,
  stickyFooterSx,
  switchSx,
} from "./melhor-envio.theme";

function StatMini({
  label,
  value,
  accent,
  ok,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
  ok?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        color={
          ok === true
            ? "success.dark"
            : ok === false
              ? "warning.dark"
              : accent
                ? ME_ACCENT
                : "text.primary"
        }
        sx={{ fontSize: { xs: "1.1rem", sm: "1.75rem" } }}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default function ConfigMelhorEnvio(): JSX.Element {
  const [, dispatch] = useMaterialUIController();
  const location = useLocation();
  const navigate = useNavigate();
  const { name } = siteConfig;

  const [config, setConfig] = useState<IMelhorEnvioConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [servicos, setServicos] = useState<IMelhorEnvioServico[]>([]);
  const [loadingServicos, setLoadingServicos] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [oauthOpening, setOauthOpening] = useState(false);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const { data, status } = await getMelhorEnvioConfig();
      if (status === 200 && data) {
        setConfig(normalizeMelhorEnvioConfig(data as unknown as Record<string, unknown>));
      } else {
        setConfig({ ...DEFAULT_MELHOR_ENVIO_CONFIG });
      }
    } catch (error: unknown) {
      const msg =
        error instanceof Error ? error.message : "Falha ao carregar configuração do Melhor Envio.";
      setMessage(dispatch, { open: true, severity: "error", text: msg });
      setConfig({ ...DEFAULT_MELHOR_ENVIO_CONFIG });
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  const loadServicos = useCallback(async () => {
    setLoadingServicos(true);
    try {
      const { data, status } = await getMelhorEnvioServicos();
      if (status === 200) {
        const raw = data as unknown;
        const list = Array.isArray(raw)
          ? raw
          : Array.isArray((raw as { data?: unknown })?.data)
            ? (raw as { data: IMelhorEnvioServico[] }).data
            : Array.isArray((raw as { servicos?: unknown })?.servicos)
              ? (raw as { servicos: IMelhorEnvioServico[] }).servicos
              : [];
        setServicos(list);
      }
    } catch (error: unknown) {
      const msg =
        error instanceof Error
          ? error.message
          : "Não foi possível carregar a lista de serviços do Melhor Envio.";
      setMessage(dispatch, { open: true, severity: "warning", text: msg });
    } finally {
      setLoadingServicos(false);
    }
  }, [dispatch]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    if (!loading && config?.id) void loadServicos();
  }, [loading, config?.id, loadServicos]);

  const onConectarOAuthMelhorEnvio = useCallback(async () => {
    setOauthOpening(true);
    try {
      const res = await getMelhorEnvioOAuthStartUrl();
      const status = (res as { status?: number }).status ?? 200;
      const body = (res as { data?: { url?: string; error?: string } }).data;
      if (status >= 400) {
        throw new Error(body?.error || "Não foi possível iniciar OAuth.");
      }
      const url = body?.url;
      if (typeof url !== "string" || !url.trim()) {
        throw new Error("Resposta sem URL de autorização.");
      }
      window.location.href = url;
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Erro ao conectar OAuth Melhor Envio.";
      setMessage(dispatch, { open: true, severity: "error", text: msg });
    } finally {
      setOauthOpening(false);
    }
  }, [dispatch]);

  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const oauthOk = q.get("me_oauth");
    const oauthErr = q.get("me_oauth_error");
    if (!oauthOk && !oauthErr) return;

    if (oauthOk === "ok") {
      setMessage(dispatch, {
        open: true,
        severity: "success",
        text: "Melhor Envio: conta autorizada (OAuth).",
      });
      q.delete("me_oauth");
      const next = q.toString();
      navigate({ pathname: location.pathname, search: next ? `?${next}` : "" }, { replace: true });
      void (async () => {
        try {
          const { data, status } = await getMelhorEnvioConfig();
          if (status === 200 && data) {
            setConfig(normalizeMelhorEnvioConfig(data as unknown as Record<string, unknown>));
          }
        } catch {
          /* ignore */
        }
      })();
      return;
    }

    if (oauthErr) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: decodeURIComponent(oauthErr),
      });
      q.delete("me_oauth_error");
      navigate(
        {
          pathname: location.pathname,
          search: q.toString() ? `?${q.toString()}` : "",
        },
        { replace: true },
      );
    }
  }, [dispatch, location.pathname, location.search, navigate]);

  const selectedServicos = useMemo(() => {
    if (!config?.servicos?.trim() || servicos.length === 0) return [];
    const ids = config.servicos
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id));
    const idSet = new Set(ids);
    return servicos.filter((s) => idSet.has(Number(s.id)));
  }, [config?.servicos, servicos]);

  const stats = useMemo(() => computeMelhorEnvioStats(config), [config]);

  function updateField<K extends keyof IMelhorEnvioConfig>(key: K, value: IMelhorEnvioConfig[K]) {
    setConfig((c) => ({ ...(c ?? DEFAULT_MELHOR_ENVIO_CONFIG), [key]: value }) as IMelhorEnvioConfig);
  }

  const onSave = useCallback(async () => {
    if (!config) return;
    const validation = validateMelhorEnvioConfig(config);
    if (validation.ok === false) {
      setMessage(dispatch, { open: true, severity: "error", text: validation.msg });
      return;
    }

    setSaving(true);
    try {
      const payload = buildMelhorEnvioPayload(config);
      const { data } = await createOrUpdateMelhorEnvioConfig(payload);
      setMessage(dispatch, {
        open: true,
        severity: "success",
        text: "Configurações do Melhor Envio salvas com sucesso.",
      });

      const merged = normalizeMelhorEnvioConfig((data || payload) as unknown as Record<string, unknown>);
      if (
        typeof (data as Record<string, unknown> | undefined)?.servicos !== "string" ||
        String((data as Record<string, unknown>)?.servicos ?? "").trim() === ""
      ) {
        merged.servicos = payload.servicos;
      }
      setConfig(merged);
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string; error?: string; errors?: string[] } };
        message?: string;
      };
      const serverMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        (Array.isArray(err?.response?.data?.errors) && err.response.data.errors.join(", ")) ||
        err?.message;
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: serverMsg || "Falha ao salvar configurações do Melhor Envio.",
      });
    } finally {
      setSaving(false);
    }
  }, [config, dispatch]);

  const onDelete = useCallback(async () => {
    setDeleting(true);
    try {
      await deleteMelhorEnvioConfig();
      setMessage(dispatch, {
        open: true,
        severity: "success",
        text: "Configurações do Melhor Envio removidas com sucesso.",
      });
      setDeleteDialogOpen(false);
      await loadConfig();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string; error?: string } }; message?: string };
      const serverMsg = err?.response?.data?.message || err?.response?.data?.error || err?.message;
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: serverMsg || "Falha ao remover configurações do Melhor Envio.",
      });
    } finally {
      setDeleting(false);
    }
  }, [dispatch, loadConfig]);

  async function handleValidateCep() {
    if (!config?.cepOrigem) return;
    try {
      const cep = await getCep(config.cepOrigem.replace(/\D/g, ""));
      if (cep.erro) {
        setMessage(dispatch, { open: true, severity: "error", text: "CEP não encontrado." });
      } else {
        setMessage(dispatch, { open: true, severity: "success", text: "CEP de origem válido." });
      }
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : "Erro ao validar CEP de origem.";
      setMessage(dispatch, { open: true, severity: "error", text: msg });
    }
  }

  if (loading) {
    return (
      <LojaPageContainer title="Frete Melhor Envio" subtitle="Carregando integração…">
        <MDBox py={8} textAlign="center">
          <CircularProgress sx={{ color: ME_ACCENT }} />
        </MDBox>
      </LojaPageContainer>
    );
  }

  return (
    <LojaPageContainer
      title="Frete Melhor Envio"
      subtitle="Integração para cotação de frete, checkout e geração de etiquetas."
      actions={
        <MDButton
          variant="gradient"
          color="dark"
          size="small"
          startIcon={<SaveOutlinedIcon />}
          onClick={() => void onSave()}
          disabled={saving || deleting}
          sx={saveButtonSx}
        >
          {saving ? "Salvando…" : "Salvar"}
        </MDButton>
      }
    >
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <StatMini
            label="Integração"
            value={stats.ativo ? "Ativa" : "Inativa"}
            accent
            ok={stats.ativo}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini
            label="Ambiente"
            value={stats.sandbox ? "Sandbox" : "Produção"}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini
            label="OAuth"
            value={stats.sandbox ? "N/A" : stats.oauth ? "Conectado" : "Pendente"}
            ok={stats.sandbox ? undefined : stats.oauth}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Serviços ativos" value={stats.servicos} />
        </Grid>
      </Grid>

      <Stack spacing={3}>
        <ConfigFormSection
          title="Configurações gerais"
          subtitle="CEP de origem, credenciais de contato e ativação da integração."
          icon={<LocalShippingOutlinedIcon />}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <MDInput
                label="CEP de origem"
                value={cepApplyMask(config?.cepOrigem || "")}
                onBlur={() => {
                  void handleValidateCep();
                }}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  updateField("cepOrigem", e.target.value)
                }
                placeholder="00000-000"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MDInput
                label="E-mail"
                type="email"
                value={config?.email || ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateField("email", e.target.value)}
                onBlur={() =>
                  updateField("userAgent", `${name} (${config?.email?.trim() || ""})`)
                }
                placeholder="seu@email.com"
                fullWidth
                disabled={!config?.ativo}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <MDInput
                label="User Agent"
                value={config?.userAgent || ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  updateField("userAgent", e.target.value)
                }
                placeholder="Aplicação (email para contato técnico)"
                fullWidth
                disabled={!config?.ativo}
              />
              <MDTypography variant="caption" color="text.secondary" display="block" mt={1}>
                Cabeçalho HTTP obrigatório: nome da aplicação + e-mail de suporte técnico.
              </MDTypography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(config?.ativo)}
                    onChange={(e) => updateField("ativo", e.target.checked)}
                    sx={switchSx}
                  />
                }
                label={
                  <Box>
                    <MDTypography variant="body2" fontWeight="medium">
                      Ativar integração com Melhor Envio
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary">
                      Desative para pausar cotações e etiquetas sem remover a configuração.
                    </MDTypography>
                  </Box>
                }
              />
            </Grid>
          </Grid>
        </ConfigFormSection>

        <ConfigFormSection
          title="Ambiente e credenciais"
          subtitle={
            config?.sandbox
              ? "Modo sandbox — use token estático para testes."
              : "Modo produção — Client ID/Secret + OAuth2."
          }
          icon={config?.sandbox ? <ScienceOutlinedIcon /> : <VpnKeyOutlinedIcon />}
        >
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(config?.sandbox)}
                    onChange={(e) => updateField("sandbox", e.target.checked)}
                    disabled={!config?.ativo}
                    sx={switchSx}
                  />
                }
                label={
                  <MDTypography variant="body2" fontWeight="medium">
                    Usar ambiente Sandbox (testes)
                  </MDTypography>
                }
              />
            </Grid>

            {config?.sandbox ? (
              <Grid item xs={12}>
                <MDInput
                  label="Token static sandbox"
                  value={config?.tokenStaticSandbox || ""}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    updateField("tokenStaticSandbox", e.target.value)
                  }
                  placeholder="Token do ambiente sandbox"
                  fullWidth
                  multiline
                  rows={4}
                  helperText="Cole o token completo do Melhor Envio (ambiente sandbox)"
                  disabled={!config?.ativo}
                />
              </Grid>
            ) : (
              <>
                <Grid item xs={12} md={6}>
                  <MDInput
                    label="Client ID"
                    value={config?.clientId || ""}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      updateField("clientId", e.target.value)
                    }
                    placeholder="Client ID da aplicação"
                    fullWidth
                    disabled={!config?.ativo}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <MDInput
                    label="Client Secret"
                    type="password"
                    value={config?.clientSecret || ""}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      updateField("clientSecret", e.target.value)
                    }
                    placeholder="Client Secret da aplicação"
                    fullWidth
                    disabled={!config?.ativo}
                  />
                </Grid>
                <Grid item xs={12}>
                  <MDBox sx={infoBannerSx}>
                    <MDTypography variant="caption" color="text.secondary" display="block" mb={1.5}>
                      No painel do Melhor Envio, configure a URL de callback OAuth (
                      <code>MELHOR_ENVIO_OAUTH_REDIRECT_URI</code>) e defina{" "}
                      <code>MELHOR_ENVIO_OAUTH_SUCCESS_URL</code> apontando para esta página (
                      <code>/loja/melhor-envio</code>).
                    </MDTypography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                      <MDButton
                        variant="outlined"
                        disabled={!config?.ativo || !config?.id || oauthOpening}
                        onClick={() => void onConectarOAuthMelhorEnvio()}
                        sx={oauthButtonSx}
                      >
                        {config?.oauthConectado
                          ? "Reautorizar OAuth"
                          : "Conectar via OAuth"}
                      </MDButton>
                      {config?.oauthConectado ? (
                        <Chip
                          size="small"
                          label="OAuth conectado"
                          sx={{
                            bgcolor: "rgba(16, 185, 129, 0.12)",
                            color: "success.dark",
                            fontWeight: 600,
                          }}
                        />
                      ) : null}
                    </Stack>
                  </MDBox>
                </Grid>
              </>
            )}
          </Grid>
        </ConfigFormSection>

        <MelhorEnvioSaldoCard enabled={Boolean(config?.ativo)} sandbox={Boolean(config?.sandbox)} />

        {config?.id ? (
          <ConfigFormSection
            title="Serviços de frete"
            subtitle="Transportadoras e modalidades disponíveis no checkout da loja."
            icon={<CloudOutlinedIcon />}
          >
            <Autocomplete
              multiple
              value={selectedServicos}
              options={servicos}
              loading={loadingServicos}
              getOptionLabel={(option) => `${option.name} – ${option.company?.name || ""}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, newValue: IMelhorEnvioServico[]) => {
                updateField("servicos", newValue.map((s) => s.id).join(","));
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <MDAvatar
                    src={option.company?.picture}
                    alt={option.company?.name}
                    size="sm"
                    sx={{ flexShrink: 0 }}
                  />
                  <Box>
                    <MDTypography variant="button" fontWeight="medium">
                      {option.name}
                    </MDTypography>
                    <MDTypography variant="caption" display="block" color="text.secondary">
                      {option.company?.name || "—"}
                    </MDTypography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <MDInput
                  {...params}
                  label="Serviços disponíveis"
                  placeholder="Selecione os serviços…"
                  InputLabelProps={{ shrink: true }}
                />
              )}
              disabled={!config?.ativo}
            />
            {selectedServicos.length > 0 ? (
              <MDTypography variant="caption" color="text.secondary" display="block" mt={1.5}>
                {selectedServicos.length} serviço{selectedServicos.length === 1 ? "" : "s"} selecionado
                {selectedServicos.length === 1 ? "" : "s"} — IDs salvos como{" "}
                <code>{config.servicos}</code>
              </MDTypography>
            ) : null}
          </ConfigFormSection>
        ) : (
          <MDBox sx={emptyStateSx}>
            <LinkOutlinedIcon sx={{ fontSize: 40, color: "grey.400", mb: 1 }} />
            <MDTypography variant="body2" color="text.secondary">
              Salve a configuração acima para selecionar os serviços de frete disponíveis.
            </MDTypography>
          </MDBox>
        )}

        <MDBox sx={stickyFooterSx}>
          <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
            <SettingsOutlinedIcon sx={{ color: ME_ACCENT, flexShrink: 0 }} />
            <MDTypography variant="button" color="text">
              Salve após alterar credenciais ou serviços. Remover apaga toda a integração.
            </MDTypography>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <MDButton
              variant="outlined"
              color="error"
              onClick={() => setDeleteDialogOpen(true)}
              disabled={saving || deleting || !config?.id}
            >
              Remover
            </MDButton>
            <MDButton variant="outlined" color="dark" onClick={() => void loadConfig()} disabled={saving || deleting}>
              Descartar alterações
            </MDButton>
            <MDButton
              variant="gradient"
              color="dark"
              startIcon={saving ? undefined : <SaveOutlinedIcon />}
              onClick={() => void onSave()}
              disabled={saving || deleting}
              sx={saveButtonSx}
            >
              {saving ? "Salvando…" : "Salvar configuração"}
            </MDButton>
          </Stack>
        </MDBox>
      </Stack>

      <MelhorEnvioDeleteDialog
        open={deleteDialogOpen}
        deleting={deleting}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={() => void onDelete()}
      />
    </LojaPageContainer>
  );
}
